import { useEffect, useRef, useState } from "react";
import { PageShell } from "./PageShell.tsx";

export function Tools() {
  const [copied, setCopied] = useState(false);
  const bookmarkletContainerRef = useRef<HTMLDivElement>(null);

  const bookmarkletCode =
    `javascript:(function(){window.open('https://kipclip.com/save?url='+encodeURIComponent(location.href),'kipclip','width=600,height=700')})()`;

  useEffect(() => {
    // Set href directly on DOM element after render to bypass React
    if (bookmarkletContainerRef.current) {
      const anchor = bookmarkletContainerRef.current.querySelector("a");
      if (anchor) {
        anchor.href = bookmarkletCode;
      }
    }
  }, [bookmarkletCode]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(bookmarkletCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }

  return (
    <PageShell>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-3">
          Save from Anywhere
        </h2>
        <p className="text-gray-600 text-lg">
          Add bookmarks to kipclip from any website with these tools
        </p>
      </div>

      {/* Bookmarklet Section */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 bg-coral/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              className="w-6 h-6"
              style={{ color: "var(--coral)" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Browser Bookmarklet
            </h3>
            <p className="text-gray-600">
              Works on all desktop browsers (Chrome, Firefox, Safari, Edge)
            </p>
          </div>
        </div>

        {/* Draggable Bookmarklet */}
        <div className="mb-6">
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-600 mb-4 font-medium">
              Drag this button to your bookmarks bar:
            </p>
            <div ref={bookmarkletContainerRef}>
              <a
                href=""
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-white shadow-lg hover:shadow-xl transition-shadow cursor-move select-none"
                style={{ backgroundColor: "var(--coral)" }}
                draggable="true"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
                Kip it
              </a>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="space-y-4 mb-6">
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">
              How to install:
            </h4>
            <ol className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">1.</span>
                <span>
                  Make sure your bookmarks bar is visible (press{" "}
                  <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-mono">
                    Cmd+Shift+B
                  </kbd>{" "}
                  or{" "}
                  <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-mono">
                    Ctrl+Shift+B
                  </kbd>)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">2.</span>
                <span>
                  Drag the "Kip it" button above to your bookmarks bar
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">3.</span>
                <span>
                  When you're on a page you want to save, click the bookmarklet
                  in your bookmarks bar
                </span>
              </li>
            </ol>
          </div>
        </div>

        {/* Alternative Methods */}
        <div className="border-t pt-6">
          <h4 className="font-semibold text-gray-800 mb-3">
            Alternative installation methods:
          </h4>
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700 mb-2">
                <strong>Method 2:</strong>{" "}
                Right-click the button above and select "Bookmark This Link" or
                "Add to Bookmarks"
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700 mb-3">
                <strong>Method 3:</strong>{" "}
                Copy the code and create a bookmark manually:
              </p>
              <div className="flex gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border text-xs overflow-x-auto">
                  {bookmarkletCode}
                </code>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition flex-shrink-0"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Section */}
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 bg-coral/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              className="w-6 h-6"
              style={{ color: "var(--coral)" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Mobile Devices
            </h3>
            <p className="text-gray-600">
              Save bookmarks from your phone or tablet
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">
              iOS Shortcut (Recommended):
            </h4>
            <p className="text-sm text-gray-700 mb-4">
              Install the "Kip it" shortcut to save bookmarks from Safari's
              share menu with one tap.
            </p>
            <a
              href="https://www.icloud.com/shortcuts/284a2a52e6cb46cc96ebed6f54fc0dfd"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-white shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: "var(--coral)" }}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Get iOS Shortcut
            </a>
            <p className="text-xs text-gray-600 mt-3">
              After installing, "Kip it" will appear in your share menu when
              viewing web pages in Safari.
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">
              iOS (Add to Home Screen):
            </h4>
            <ol className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">1.</span>
                <span>Open kipclip.com in Safari</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">2.</span>
                <span>
                  Tap the Share button (square with arrow), then "Add to Home
                  Screen"
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">3.</span>
                <span>
                  kipclip opens as a standalone app - use the iOS Shortcut above
                  to save bookmarks from Safari
                </span>
              </li>
            </ol>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">
              Android (Chrome):
            </h4>
            <ol className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">1.</span>
                <span>Open kipclip.com in Chrome</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">2.</span>
                <span>
                  Tap the menu (...), then "Add to Home screen" or "Install app"
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-coral min-w-[20px]">3.</span>
                <span>
                  kipclip opens as a standalone app and appears in your share
                  menu - share URLs directly from any app
                </span>
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* More Tools */}
      <div className="bg-white rounded-lg shadow-md p-8 mt-8">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 bg-coral/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              className="w-6 h-6"
              style={{ color: "var(--coral)" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              More Tools
            </h3>
            <p className="text-gray-600">
              More ways to work with your kipclip bookmarks
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">kip CLI</h4>
            <p className="text-sm text-gray-700">
              Add, search, tag, and manage your bookmarks from the terminal. kip
              talks directly to your PDS and uses the same bookmark data as
              kipclip.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <code className="bg-white px-3 py-2 border rounded text-xs">
                brew install tijs/tap/kipclip
              </code>
              <a
                href="https://github.com/tijs/kipclip-cli"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium hover:underline"
                style={{ color: "var(--coral)" }}
              >
                View on GitHub
              </a>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">
                  Airglow
                </h4>
                <p className="text-sm text-gray-700">
                  Automatically bookmark links from posts you like on Bluesky.
                  When you like a post that contains a link, Airglow saves that
                  link to kipclip for you.
                </p>
              </div>
            </div>
            <a
              href="https://airglow.run/u/kipclip.com/3mlxzkcr4rs22"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-sm font-medium hover:underline"
              style={{ color: "var(--coral)" }}
            >
              Learn more on airglow.run
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 text-center text-sm text-gray-600">
        <p>
          Need help?{" "}
          <a
            href="/"
            className="font-medium hover:underline"
            style={{ color: "var(--coral)" }}
          >
            Contact us
          </a>
        </p>
      </div>
    </PageShell>
  );
}
